// Небольшие графики «Стройки» на SVG без библиотек: кольцо готовности, план/факт по этажам, цикл этажа по этажам,
// график этажей по датам. Цвета — токены из styles.css (--ser-1, --ser-2, --fill-*); подписи — цветом текста.

import { useState, type ReactNode } from "react";
import { fmtDate, fmtNum } from "../shared/format";
import { floorName, floorShort } from "./ctx";

/** Кольцо готовности: доля выполненного, по центру — процент. */
export function Ring({ value, size = 76, label }: { value: number; size?: number; label?: string }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label || `${Math.round(v)}%`} className="ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line-soft)" strokeWidth={8} />
      {v > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--st-done)" strokeWidth={8} strokeLinecap="round"
        strokeDasharray={`${(c * v) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size / 4.2} fontWeight={700} fill="var(--ink)">{Math.round(v)}%</text>
    </svg>
  );
}

function Tip({ tip, children }: { tip: { x: number; y: number; text: ReactNode } | null; children: ReactNode }) {
  return (
    <div className="chart-box">
      {children}
      {tip && <div className="chart-tip" style={{ left: `${tip.x}%`, top: `${tip.y}%` }}>{tip.text}</div>}
    </div>
  );
}

/** План и факт по этажам: серый столбец — план, синий — факт (величина против цели, одна ось). */
export function PlanFactBars({ rows, unit }: { rows: { floor: number; plan: number; fact: number }[]; unit: string }) {
  const [tip, setTip] = useState<{ x: number; y: number; text: ReactNode } | null>(null);
  const W = 320, H = 150, L = 34, B = 20, T = 8;
  const max = Math.max(1, ...rows.flatMap((r) => [r.plan, r.fact])) * 1.1;
  const step = (W - L - 4) / Math.max(1, rows.length);
  const bw = Math.min(14, step / 3);
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const ticks = [0, max / 2, max].map((v) => Math.round(v / 10) * 10);
  return (
    <Tip tip={tip}>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label={`План и факт, ${unit}, по этажам`} onMouseLeave={() => setTip(null)}>
        {ticks.map((v) => <g key={v}><line x1={L} x2={W - 4} y1={y(v)} y2={y(v)} className="grid" /><text x={L - 4} y={y(v)} className="ax" textAnchor="end" dominantBaseline="central">{v}</text></g>)}
        {rows.map((r, i) => {
          const cx = L + step * i + step / 2;
          return (
            <g key={r.floor} onMouseEnter={() => setTip({ x: (cx / W) * 100, y: 0, text: <><b>{floorName(r.floor)}</b><br />план {fmtNum(r.plan, 0)} {unit}<br />факт {fmtNum(r.fact, 0)} {unit}</> })}>
              <rect x={cx - step / 2} y={T} width={step} height={H - T - B} fill="transparent" />
              <rect x={cx - bw - 1} y={y(r.plan)} width={bw} height={Math.max(0, H - B - y(r.plan))} rx={3} className="b-plan" />
              <rect x={cx + 1} y={y(r.fact)} width={bw} height={Math.max(0, H - B - y(r.fact))} rx={3} className="b-fact" />
              <text x={cx} y={H - 6} className="ax" textAnchor="middle">{floorShort(r.floor)}</text>
            </g>
          );
        })}
      </svg>
    </Tip>
  );
}

/** Цикл этажа по этажам для каждого корпуса; пунктир — план. Последняя точка идущего этажа — прогноз (полая). */
export function CycleLines({ series, norm }: { series: { name: string; points: { floor: number; days: number | null; forecast?: boolean }[] }[]; norm: number | null }) {
  const [tip, setTip] = useState<{ x: number; y: number; text: ReactNode } | null>(null);
  const W = 420, H = 180, L = 30, B = 22, T = 10, R = 8;
  const floors = Math.max(2, ...series.flatMap((s) => s.points.map((p) => p.floor)));
  const vals = series.flatMap((s) => s.points.map((p) => p.days).filter((v): v is number => v != null));
  const lo = Math.floor(Math.min(norm ?? 99, ...vals, 99) - 1);
  const hi = Math.ceil(Math.max(norm ?? 0, ...vals, 0) + 1);
  const x = (f: number) => L + ((W - L - R) * (f - 1)) / (floors - 1);
  const y = (v: number) => T + ((H - T - B) * (hi - v)) / Math.max(1, hi - lo);
  const yt: number[] = [];
  for (let v = lo; v <= hi; v += Math.max(1, Math.round((hi - lo) / 4))) yt.push(v);
  return (
    <Tip tip={tip}>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label="Цикл этажа в днях по этажам" onMouseLeave={() => setTip(null)}>
        {yt.map((v) => <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="grid" /><text x={L - 4} y={y(v)} className="ax" textAnchor="end" dominantBaseline="central">{v}</text></g>)}
        {Array.from({ length: floors }, (_, i) => i + 1).filter((f) => floors <= 12 || f % 2 === 1 || f === floors).map((f) => <text key={f} x={x(f)} y={H - 6} className="ax" textAnchor="middle">{f}</text>)}
        {norm != null && <><line x1={L} x2={W - R} y1={y(norm)} y2={y(norm)} className="norm" /><text x={W - R} y={y(norm) - 4} className="ax" textAnchor="end">план {fmtNum(norm, 0)} дн.</text></>}
        {series.map((s, si) => {
          const pts = s.points.filter((p) => p.days != null) as { floor: number; days: number; forecast?: boolean }[];
          return (
            <g key={s.name} className={`ser-${si + 1}`}>
              <polyline fill="none" strokeWidth={2} points={pts.map((p) => `${x(p.floor)},${y(p.days)}`).join(" ")} className="ln" />
              {pts.map((p) => (
                <circle key={p.floor} cx={x(p.floor)} cy={y(p.days)} r={4} className={p.forecast ? "pt hollow" : "pt"}
                  onMouseEnter={() => setTip({ x: (x(p.floor) / W) * 100, y: (y(p.days) / H) * 100, text: <><b>{s.name}, {floorName(p.floor, false)}</b><br />{p.forecast ? "прогноз" : "факт"} {p.days} дн.</> })} />
              ))}
            </g>
          );
        })}
      </svg>
    </Tip>
  );
}

type FloorBar = { floor: number; status: string; planned_start: string; planned_end: string; actual_start: string | null; actual_end: string | null; forecast_end: string | null };

/** Производственный график по этажам: серая полоса — план, поверх — факт (зелёный — выполнен, синий — идёт,
 * жёлтый — идёт позже плана), пунктир — прогноз; вертикаль — сегодня. */
export function FloorsGantt({ rows, today, days = 24 }: { rows: FloorBar[]; today: string; days?: number }) {
  const [tip, setTip] = useState<{ x: number; y: number; text: ReactNode } | null>(null);
  const start = new Date(today); start.setDate(start.getDate() - Math.round(days * 0.55));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const dates = Array.from({ length: days }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return iso(d); });
  const first = dates[0], last = dates[dates.length - 1];
  const visible = rows.filter((r) => (r.actual_end || r.forecast_end || r.planned_end) >= first && (r.actual_start || r.planned_start) <= last);
  const W = 640, L = 56, RH = 22, T = 18;
  const H = T + visible.length * RH + 4;
  const dx = (W - L - 4) / days;
  const px = (d: string) => L + Math.max(0, Math.min(days, (Date.parse(d) - Date.parse(first)) / 86400000)) * dx;
  const span = (a: string, b: string) => ({ x: px(a), w: Math.max(2, px(b) + dx - px(a)) });
  return (
    <Tip tip={tip}>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label="График этажей по датам" onMouseLeave={() => setTip(null)}>
        {dates.map((d, i) => i % 2 === 0 && <text key={d} x={L + dx * i + dx / 2} y={11} className="ax" textAnchor="middle">{Number(d.slice(8))}</text>)}
        {today >= first && today <= last && <line x1={px(today) + dx / 2} x2={px(today) + dx / 2} y1={T - 2} y2={H} className="today" />}
        {visible.map((r, i) => {
          const yy = T + i * RH;
          const p = span(r.planned_start, r.planned_end);
          const late = (r.forecast_end || r.planned_end) > r.planned_end;
          const cls = r.status === "done" ? "g-done" : late ? "g-late" : "g-run";
          const f = r.actual_start ? span(r.actual_start, r.actual_end || (today < r.planned_start ? r.actual_start : today)) : null;
          const fc = r.status !== "done" && r.forecast_end && r.forecast_end > today ? span(today > (r.actual_start || r.planned_start) ? today : (r.actual_start || r.planned_start), r.forecast_end) : null;
          const text = <><b>{floorName(r.floor)}</b><br />план {fmtDate(r.planned_start)}–{fmtDate(r.planned_end)}{r.actual_start ? <><br />факт с {fmtDate(r.actual_start)}{r.actual_end ? ` по ${fmtDate(r.actual_end)}` : ""}</> : null}{r.status !== "done" && r.forecast_end ? <><br />прогноз {fmtDate(r.forecast_end)}</> : null}</>;
          return (
            <g key={r.floor} onMouseEnter={() => setTip({ x: ((p.x + p.w / 2) / W) * 100, y: (yy / H) * 100, text })}>
              <rect x={0} y={yy} width={W} height={RH} fill="transparent" />
              <text x={4} y={yy + RH / 2} className="lbl" dominantBaseline="central">{floorName(r.floor)}</text>
              <rect x={p.x} y={yy + 5} width={p.w} height={RH - 10} rx={4} className="g-plan" />
              {fc && <rect x={fc.x} y={yy + 4} width={fc.w} height={RH - 8} rx={4} className="g-fc" />}
              {f && <rect x={f.x} y={yy + 4} width={f.w} height={RH - 8} rx={4} className={cls} />}
            </g>
          );
        })}
      </svg>
    </Tip>
  );
}
